// NobleSwipeCard - Swipeable card for mobile-first interactions
// Swipe Right: Primary action (green) - Mark Complete, Approve
// Swipe Left: Secondary action (blue) - Reassign, Edit
// Features: Haptic feedback, snap animations, action icons

import React, { useState, useRef } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSpring, animated, config } from '@react-spring/web';
import styled from '@emotion/styled';
import { Card, Typography } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
  RightOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows } from '../../../theme/noble-tokens';

const { Text } = Typography;

export interface SwipeAction {
  icon: React.ReactNode;
  label: string;
  color: string;
  onAction: () => void | Promise<void>;
}

export interface NobleSwipeCardProps {
  children: React.ReactNode;
  rightAction?: SwipeAction;
  leftAction?: SwipeAction;
  onSwipeRight?: () => void | Promise<void>;
  onSwipeLeft?: () => void | Promise<void>;
  swipeThreshold?: number; // px to trigger action
  disabled?: boolean;
  hapticFeedback?: boolean;
  className?: string;
}

// Default actions
const defaultRightAction: SwipeAction = {
  icon: <CheckCircleOutlined />,
  label: 'Complete',
  color: NobleColors['operational-green-base'],
  onAction: () => console.log('Right swipe action'),
};

const defaultLeftAction: SwipeAction = {
  icon: <EditOutlined />,
  label: 'Edit',
  color: NobleColors['command-blue-base'],
  onAction: () => console.log('Left swipe action'),
};

// Styled Components
const SwipeContainer = styled.div`
  position: relative;
  width: 100%;
  touch-action: pan-y; // Allow vertical scrolling, handle horizontal
  user-select: none;
  overflow: hidden;
`;

const ActionBackground = styled.div<{ $side: 'left' | 'right'; $color: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $side }) => ($side === 'right' ? 'left: 0;' : 'right: 0;')}
  width: 120px;
  background: ${({ $color }) => $color};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${NobleSpacing.xxs};
  color: white;
  font-size: 14px;
  font-weight: 600;
  z-index: 0;
  border-radius: ${({ $side }) => ($side === 'right' ? '12px 0 0 12px' : '0 12px 12px 0')};

  .action-icon {
    font-size: 32px;
  }

  .action-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const AnimatedCard = styled(animated.div)`
  position: relative;
  z-index: 1;
  will-change: transform;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  .ant-card {
    border-radius: 12px;
    box-shadow: ${NobleShadows['elevation-1']};
    transition: box-shadow 0.2s ease-out;
    margin-bottom: 0;
  }

  &.swiping {
    .ant-card {
      box-shadow: ${NobleShadows['elevation-3']};
    }
  }
`;

const SwipeIndicator = styled.div<{ $visible: boolean; $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $direction }) => ($direction === 'right' ? 'right: 16px;' : 'left: 16px;')}
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${NobleColors['steel-gray-dark']};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease-out;
  pointer-events: none;
  z-index: 10;
`;

// Haptic feedback helper
const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'medium') => {
  if ('vibrate' in navigator) {
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 30,
    };
    navigator.vibrate(patterns[type]);
  }
};

// Main Component
export const NobleSwipeCard: React.FC<NobleSwipeCardProps> = ({
  children,
  rightAction = defaultRightAction,
  leftAction = defaultLeftAction,
  onSwipeRight,
  onSwipeLeft,
  swipeThreshold = 100, // px
  disabled = false,
  hapticFeedback = true,
  className,
}) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [hasTriggeredHaptic, setHasTriggeredHaptic] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Spring animation for card position
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: config.stiff,
  }));

  // Drag gesture handler
  const bind = useDrag(
    ({ down, movement: [mx], velocity: [vx], direction: [dx], cancel, last }) => {
      if (disabled) return;

      setIsSwiping(down);

      // Calculate if we've passed the threshold
      const isRightSwipe = mx > swipeThreshold;
      const isLeftSwipe = mx < -swipeThreshold;
      const triggered = isRightSwipe || isLeftSwipe;

      // Haptic feedback when crossing threshold
      if (hapticFeedback && triggered && !hasTriggeredHaptic && down) {
        triggerHaptic('medium');
        setHasTriggeredHaptic(true);
      }

      // Reset haptic flag when going back below threshold
      if (!triggered && hasTriggeredHaptic) {
        setHasTriggeredHaptic(false);
      }

      // If user releases and we've triggered an action
      if (last && triggered) {
        if (isRightSwipe) {
          // Swipe right action
          api.start({
            x: window.innerWidth, // Swipe off screen
            config: config.wobbly,
            onRest: async () => {
              if (hapticFeedback) triggerHaptic('heavy');
              if (onSwipeRight) await onSwipeRight();
              if (rightAction?.onAction) await rightAction.onAction();
              // Reset position after action (card might be removed by parent)
              api.start({ x: 0, immediate: true });
            },
          });
        } else if (isLeftSwipe) {
          // Swipe left action
          api.start({
            x: -window.innerWidth, // Swipe off screen
            config: config.wobbly,
            onRest: async () => {
              if (hapticFeedback) triggerHaptic('heavy');
              if (onSwipeLeft) await onSwipeLeft();
              if (leftAction?.onAction) await leftAction.onAction();
              // Reset position after action
              api.start({ x: 0, immediate: true });
            },
          });
        }
        cancel(); // Cancel the gesture
        return;
      }

      // Animate card position while dragging
      if (down) {
        // Add resistance at edges
        const resistance = 0.3;
        const resistedMx = mx * (Math.abs(mx) > swipeThreshold ? resistance : 1);
        api.start({ x: resistedMx, immediate: true });
      } else {
        // Snap back if not triggered
        api.start({ x: 0, config: config.wobbly });
        setHasTriggeredHaptic(false);
      }
    },
    {
      axis: 'x', // Only horizontal movement
      filterTaps: true, // Ignore taps
      rubberband: true, // Add rubber band effect at edges
    }
  );

  // Calculate opacity for action backgrounds
  const rightOpacity = x.to({
    range: [0, swipeThreshold],
    output: [0, 1],
    extrapolate: 'clamp',
  });

  const leftOpacity = x.to({
    range: [-swipeThreshold, 0],
    output: [1, 0],
    extrapolate: 'clamp',
  });

  // Show indicators when swiping
  const showRightIndicator = x.to((val) => val > swipeThreshold * 0.5);
  const showLeftIndicator = x.to((val) => val < -swipeThreshold * 0.5);

  return (
    <SwipeContainer ref={containerRef} className={className}>
      {/* Right action background (visible when swiping right) */}
      {rightAction && (
        <ActionBackground $side="right" $color={rightAction.color} style={{ opacity: rightOpacity as any }}>
          <div className="action-icon">{rightAction.icon}</div>
          <div className="action-label">{rightAction.label}</div>
        </ActionBackground>
      )}

      {/* Left action background (visible when swiping left) */}
      {leftAction && (
        <ActionBackground $side="left" $color={leftAction.color} style={{ opacity: leftOpacity as any }}>
          <div className="action-icon">{leftAction.icon}</div>
          <div className="action-label">{leftAction.label}</div>
        </ActionBackground>
      )}

      {/* Animated card */}
      <AnimatedCard
        {...bind()}
        style={{ x }}
        className={isSwiping ? 'swiping' : ''}
      >
        <Card bordered={false}>
          {children}
          
          {/* Swipe indicators */}
          <animated.div style={{ display: showRightIndicator.to((show) => (show ? 'block' : 'none')) }}>
            <SwipeIndicator $visible={true} $direction="right">
              <RightOutlined />
            </SwipeIndicator>
          </animated.div>
          
          <animated.div style={{ display: showLeftIndicator.to((show) => (show ? 'block' : 'none')) }}>
            <SwipeIndicator $visible={true} $direction="left">
              <LeftOutlined />
            </SwipeIndicator>
          </animated.div>
        </Card>
      </AnimatedCard>
    </SwipeContainer>
  );
};

// Pre-configured swipe cards for common use cases

interface WorkOrderSwipeCardProps extends Omit<NobleSwipeCardProps, 'rightAction' | 'leftAction'> {
  onComplete?: () => void;
  onReassign?: () => void;
}

export const WorkOrderSwipeCard: React.FC<WorkOrderSwipeCardProps> = ({ onComplete, onReassign, ...props }) => (
  <NobleSwipeCard
    {...props}
    rightAction={{
      icon: <CheckCircleOutlined />,
      label: 'Complete',
      color: NobleColors['operational-green-base'],
      onAction: onComplete || (() => console.log('Work order completed')),
    }}
    leftAction={{
      icon: <SwapOutlined />,
      label: 'Reassign',
      color: NobleColors['command-blue-base'],
      onAction: onReassign || (() => console.log('Reassign work order')),
    }}
  />
);

interface InspectionSwipeCardProps extends Omit<NobleSwipeCardProps, 'rightAction' | 'leftAction'> {
  onApprove?: () => void;
  onReject?: () => void;
}

export const InspectionSwipeCard: React.FC<InspectionSwipeCardProps> = ({ onApprove, onReject, ...props }) => (
  <NobleSwipeCard
    {...props}
    rightAction={{
      icon: <CheckCircleOutlined />,
      label: 'Approve',
      color: NobleColors['operational-green-base'],
      onAction: onApprove || (() => console.log('Inspection approved')),
    }}
    leftAction={{
      icon: <DeleteOutlined />,
      label: 'Reject',
      color: NobleColors['critical-red-base'],
      onAction: onReject || (() => console.log('Inspection rejected')),
    }}
  />
);

interface TaskSwipeCardProps extends Omit<NobleSwipeCardProps, 'rightAction' | 'leftAction'> {
  onComplete?: () => void;
  onEdit?: () => void;
}

export const TaskSwipeCard: React.FC<TaskSwipeCardProps> = ({ onComplete, onEdit, ...props }) => (
  <NobleSwipeCard
    {...props}
    rightAction={{
      icon: <CheckCircleOutlined />,
      label: 'Done',
      color: NobleColors['operational-green-base'],
      onAction: onComplete || (() => console.log('Task completed')),
    }}
    leftAction={{
      icon: <EditOutlined />,
      label: 'Edit',
      color: NobleColors['command-blue-base'],
      onAction: onEdit || (() => console.log('Edit task')),
    }}
  />
);

